import { onUnmounted, ref, type Ref } from 'vue';

/**
 * Keeps a scrollable region's scrollbar invisible at rest, fading it in only
 * while the user is actually scrolling. CSS alone can't observe a scroll event,
 * so the flag is toggled here and cleared shortly after scrolling stops.
 *
 * Pair with the `auto-hide-scrollbar` SCSS mixin: bind `isScrolling` to the
 * element's `--scrolling` modifier and `onScroll` to `@scroll.passive`.
 */
export function useAutoHideScrollbar(hideDelay = 800): {
  isScrolling: Ref<boolean>;
  onScroll: () => void;
} {
  const isScrolling = ref(false);
  let timer: ReturnType<typeof setTimeout> | undefined;

  function onScroll() {
    isScrolling.value = true;
    clearTimeout(timer);
    timer = setTimeout(() => {
      isScrolling.value = false;
    }, hideDelay);
  }

  onUnmounted(() => clearTimeout(timer));

  return { isScrolling, onScroll };
}
